import React from 'react';
import { Box, Flex, Text, VStack, Spinner, Icon } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, PlusCircle, FileText, ClipboardText, ArrowRight } from 'phosphor-react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useEventos, IEventoHistorico } from '../../../../hooks/useEventos';
import { IDataNoPagination } from '../../../../types/shared';

const ACAO_CONFIG: Record<string, { color: string; icon: any }> = {
  evento_criado: { color: 'blue', icon: PlusCircle },
  contrato_adicionado: { color: 'cyan', icon: FileText },
  status_alterado: { color: 'purple', icon: ArrowRight },
  checklist_concluido: { color: 'green', icon: CheckCircle },
  documento_adicionado: { color: 'orange', icon: ClipboardText },
};

interface HistoricoTabProps {
  eventoId: number;
}

export default function HistoricoTab({ eventoId }: HistoricoTabProps) {
  const { getHistorico } = useEventos();

  const { data: historicoData, isFetching } = useQuery<IDataNoPagination<IEventoHistorico[]>>({
    queryKey: ['evento-historico', eventoId],
    queryFn: getHistorico,
  });

  const items = historicoData?.data ?? [];

  if (isFetching) {
    return (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box>
      <VStack spacing={0} align="stretch">
        {items.map(item => {
          const cfg = ACAO_CONFIG[item.acao] || ACAO_CONFIG.evento_criado;
          return (
            <Flex
              key={item.id}
              align="start"
              gap={3}
              py={3}
              borderBottom="1px solid"
              borderColor="gray.50"
            >
              <Box mt={0.5}>
                <Icon as={cfg.icon} color={`${cfg.color}.400`} boxSize={5} weight="fill" />
              </Box>
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="500" color="gray.700">
                  {item.descricao}
                </Text>
                {item.usuario_nome && (
                  <Text fontSize="2xs" color="gray.400" mt={0.5}>
                    por {item.usuario_nome}
                  </Text>
                )}
              </Box>
              {item.dt_criacao && (
                <Text fontSize="2xs" color="gray.400" flexShrink={0}>
                  {moment(item.dt_criacao).format('DD MMM · HH:mm')}
                </Text>
              )}
            </Flex>
          );
        })}
      </VStack>

      {items.length === 0 && (
        <Flex justify="center" py={8}>
          <Text color="gray.400" fontSize="sm">
            Nenhum registro no histórico
          </Text>
        </Flex>
      )}
    </Box>
  );
}
